/**
 * Browser side of server-built download ZIPs: ask the scope's route to
 * build the ZIP, poll it by repeating the request, then hand the signed
 * R2 URL to the browser's own download manager. That download does not
 * depend on this tab staying open, and the browser can resume it.
 */

import {
  isDownloadZipJobView,
  type DownloadZipJobView,
} from "@/lib/virtualspace/download-zip-view";

export type ServerZipProgress = Extract<DownloadZipJobView, { mode: "job" }>;

export type ServerZipResult =
  | { outcome: "stream" }
  | { outcome: "started"; fileName: string; fileSize: number | null };

const POLL_MIN_MS = 3_000;
const POLL_MAX_MS = 10_000;
/** Polls in a row that may fail (network blip, deploy) before giving up. */
const POLL_ERROR_BUDGET = 6;

/**
 * Routes whose last answer was `stream`. The caller falls back to its
 * picker-based ZIP; if that fallback loses the click's user activation
 * to this round trip, the next click skips the round trip.
 */
const streamOnly = new Set<string>();

export function serverZipKnownToStream(requestUrl: string): boolean {
  return streamOnly.has(requestUrl);
}

function startNativeDownload(url: string, fileName: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

async function ask(
  fetchImpl: typeof fetch,
  requestUrl: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<DownloadZipJobView> {
  const res = await fetchImpl(requestUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    cache: "no-store",
    signal,
  });
  const data = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const error =
      data && typeof data === "object" && typeof (data as { error?: unknown }).error === "string"
        ? (data as { error: string }).error
        : `ZIP request failed (HTTP ${res.status})`;
    throw Object.assign(new Error(error), { status: res.status });
  }
  if (!isDownloadZipJobView(data)) throw new Error("Unexpected response from the ZIP service.");
  return data;
}

export async function runServerZipDownload(args: {
  requestUrl: string;
  body?: Record<string, unknown>;
  onProgress?: (p: ServerZipProgress) => void;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
  startDownload?: (url: string, fileName: string) => void;
}): Promise<ServerZipResult> {
  const fetchImpl = args.fetchImpl ?? fetch;
  const sleep = args.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const startDownload = args.startDownload ?? startNativeDownload;

  let view = await ask(fetchImpl, args.requestUrl, { ...args.body, retry: true }, args.signal);
  let delay = POLL_MIN_MS;
  let errors = 0;
  for (;;) {
    if (view.mode === "stream") {
      streamOnly.add(args.requestUrl);
      return { outcome: "stream" };
    }
    streamOnly.delete(args.requestUrl);
    args.onProgress?.(view);
    if (view.status === "ready" && view.url) {
      startDownload(view.url, view.fileName);
      return { outcome: "started", fileName: view.fileName, fileSize: view.fileSize };
    }
    if (view.status === "failed") {
      throw new Error(view.error ?? "The ZIP build failed.");
    }
    await sleep(delay);
    delay = Math.min(POLL_MAX_MS, Math.round(delay * 1.5));
    if (args.signal?.aborted) throw new DOMException("Aborted", "AbortError");
    try {
      view = await ask(fetchImpl, args.requestUrl, { ...args.body, retry: false }, args.signal);
      errors = 0;
    } catch (err) {
      const status = (err as { status?: unknown }).status;
      const permanent = typeof status === "number" && status >= 400 && status < 500;
      errors += 1;
      if (permanent || errors >= POLL_ERROR_BUDGET || args.signal?.aborted) throw err;
    }
  }
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  return `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`;
}

/** One-line status for a server-built ZIP. */
export function serverZipProgressLabel(p: ServerZipProgress): string {
  if (p.status === "ready") return "ZIP ready — downloading";
  if (p.queued || p.status === "pending") return "Waiting for a free ZIP builder…";
  const total = p.bytesTotal && p.bytesTotal > 0 ? ` of ${formatBytes(p.bytesTotal)}` : "";
  const done = p.bytesDone > 0 ? ` ${formatBytes(p.bytesDone)}${total}` : "";
  return `Building ZIP on the server…${done}`;
}
